import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Mail, CheckCircle, Loader2, ArrowLeft } from 'lucide-react';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import AnimatedBackground from '../components/AnimatedBackground';
import api from '../services/api';

const ForgotPasswordPage = () => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim()) { toast.error('Veuillez saisir votre email'); return; }
    setLoading(true);
    try {
      await api.post('/auth/forgot-password', { email });
      setSent(true);
    } catch {
      setSent(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <AnimatedBackground>
      <div className="min-h-screen flex items-center justify-center p-4" data-testid="forgot-password-page">
        <div className="w-full max-w-md">
          <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} className="text-center mb-8">
            <Link to="/"><img src="/logo-light.png" alt="iBookSport" className="h-12 mx-auto" /></Link>
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}
            className="bg-white/[0.04] backdrop-blur-xl border border-white/[0.08] overflow-hidden">
            <div className="bg-brand px-8 py-5">
              <h1 className="font-heading text-xl font-black uppercase tracking-wider text-white">
                {sent ? 'Email envoye' : 'Mot de passe oublie'}
              </h1>
            </div>

            {sent ? (
              <div className="p-8 text-center">
                <CheckCircle className="w-16 h-16 text-emerald-400 mx-auto mb-4" />
                <p className="text-white text-sm mb-2">Si un compte existe avec cet email, vous recevrez un lien de reinitialisation.</p>
                <p className="text-slate-500 text-xs mb-6">Verifiez aussi vos spams.</p>
                <Link to="/login" className="text-brand font-heading font-bold text-xs uppercase tracking-wider hover:underline">
                  Retour a la connexion
                </Link>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="p-8 space-y-5">
                <p className="text-slate-400 text-sm">Saisissez votre adresse email. Nous vous enverrons un lien pour reinitialiser votre mot de passe.</p>
                <div>
                  <Label className="text-[10px] font-heading uppercase tracking-widest text-slate-400 mb-1.5 block">Email</Label>
                  <div className="relative">
                    <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-500 w-4 h-4" />
                    <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="votre@email.com"
                      className="w-full h-12 pl-11 pr-4 bg-white/[0.06] border border-white/[0.1] text-white placeholder:text-slate-600 outline-none text-sm focus:border-brand/60 transition-all"
                      data-testid="forgot-email-input" />
                  </div>
                </div>
                <button type="submit" disabled={loading} data-testid="forgot-submit-btn"
                  className="w-full h-12 bg-brand hover:bg-brand/90 text-white font-heading font-bold text-xs uppercase tracking-widest flex items-center justify-center gap-2 disabled:opacity-50">
                  {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Envoyer le lien'}
                </button>
                <div className="text-center">
                  <Link to="/login" className="inline-flex items-center gap-1 text-xs text-slate-500 hover:text-brand transition-colors" data-testid="forgot-back-login">
                    <ArrowLeft className="w-3 h-3" /> Retour a la connexion
                  </Link>
                </div>
              </form>
            )}
          </motion.div>
        </div>
      </div>
    </AnimatedBackground>
  );
};

export default ForgotPasswordPage;
